import { DataType, typeAMatchesTypeB } from './DataType';
import { FunctionDefinition, UserDefinedType } from './Graph';
import { dictionaryFromStringTuple } from '../helpers/dictionaryFromTuple';

export interface ShapeValue {
    isShapeValue: true;
    type: DataType;
}

export interface FunctionReferenceValue {
    isFunctionReferenceValue: true;
    functionId: string;
}

export interface SecretReferenceValue {
    isSecretReferenceValue: true;
    secretName: string;
}

export type DataValue =
    | number
    | string
    | boolean
    | { [key: string]: DataValue }
    | DataValue[]
    | null
    | ShapeValue
    | FunctionReferenceValue
    | SecretReferenceValue;

export function getDefaultValueForDataType(dataType: DataType): DataValue {
    switch (dataType.kind) {
        case 'generic':
            if (dataType.resolvedType) {
                return getDefaultValueForDataType(dataType.resolvedType);
            }
            return null;
        case 'string':
            return '';
        case 'number':
            return 0;
        case 'boolean':
            return false;
        case 'stringLiteral':
            return dataType.stringLiteral;
        case 'numberLiteral':
            return dataType.numberLiteral;
        case 'object':
            return dictionaryFromStringTuple(
                Object.entries(dataType.properties).filter(([property]) => !dataType.optionalProperties?.includes(property)),
                ([key, valueType]) => [key, getDefaultValueForDataType(valueType)],
            );
        case 'union':
            return getDefaultValueForDataType(dataType.subTypes[0]);
        case 'array':
            return [];
        case 'null':
            return null;
        case 'shape':
            if (dataType.restriction) {
                return {
                    isShapeValue: true,
                    type: dataType.restriction,
                };
            } else {
                return {
                    isShapeValue: true,
                    type: { kind: 'string' },
                };
            }
        case 'optional':
            return getDefaultValueForDataType(dataType.innerType);
        case 'function':
            return null;
        case 'secret':
            return { isSecretReferenceValue: true, secretName: '' };
        default:
            throw new Error(`Unknown DataType kind: ${(dataType as any).kind}`);
    }
}

export function isShapeValue(value: DataValue): value is ShapeValue {
    return typeof value === 'object' && !Array.isArray(value) && value !== null && 'isShapeValue' in value && value.isShapeValue === true;
}

export function isFunctionReferenceValue(value: DataValue): value is FunctionReferenceValue {
    return (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value !== null &&
        'isFunctionReferenceValue' in value &&
        value.isFunctionReferenceValue === true
    );
}

export function isSecretReferenceValue(value: DataValue): value is SecretReferenceValue {
    return (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value !== null &&
        'isSecretReferenceValue' in value &&
        value.isSecretReferenceValue === true
    );
}

export function matchesDataType(
    value: DataValue,
    type: DataType,
    userDefinedType?: UserDefinedType,
    functionRegistry?: FunctionDefinition[],
): boolean {
    switch (type.kind) {
        case 'string':
            return typeof value === 'string';
        case 'number':
            return typeof value === 'number';
        case 'boolean':
            return typeof value === 'boolean';
        case 'stringLiteral':
            return typeof value === 'string' && value === type.stringLiteral;
        case 'numberLiteral':
            return typeof value === 'number' && value === type.numberLiteral;
        case 'null':
            return value === null;
        case 'optional':
            return value === null || matchesDataType(value, type.innerType, userDefinedType, functionRegistry);
        case 'secret':
            return isSecretReferenceValue(value);
        case 'object':
            return (
                typeof value === 'object' &&
                !Array.isArray(value) &&
                value !== null &&
                !isShapeValue(value) &&
                !isFunctionReferenceValue(value) &&
                !isSecretReferenceValue(value) &&
                Object.entries(type.properties).every(([propertyKey, propertyType]) => {
                    const correspondingPropertyOnValue: DataValue | undefined = value[propertyKey];
                    const isOptional = type.optionalProperties?.includes(propertyKey);
                    if (isOptional && correspondingPropertyOnValue === undefined) {
                        return true;
                    }

                    return (
                        correspondingPropertyOnValue !== undefined &&
                        matchesDataType(correspondingPropertyOnValue, propertyType, undefined, functionRegistry)
                    );
                })
            );
        case 'array':
            return Array.isArray(value) && value.every(subValue => matchesDataType(subValue, type.subType, undefined, functionRegistry));
        case 'union':
            if (userDefinedType && userDefinedType.kind === 'union') {
                return matchesDataType(value, type.subTypes[userDefinedType.selectedTypeIndex], undefined, functionRegistry);
            }
            return matchesDataType(value, type.subTypes[0], undefined, functionRegistry);
        case 'shape':
            // TODO Look at this later
            return true;
        case 'generic':
            if (type.resolvedType) {
                return matchesDataType(value, type.resolvedType, userDefinedType, functionRegistry);
            }
            return false;
        case 'function': {
            // Check if value is a FunctionReferenceValue
            if (!isFunctionReferenceValue(value)) {
                return false;
            }

            // If no registry provided, we can't validate the function signature
            if (!functionRegistry) {
                // Could only check that it's a valid function reference
                return true;
            }

            // Look up the function definition from the registry
            const funcDef = functionRegistry.find(f => f.id === value.functionId);
            if (!funcDef) {
                return false; // Function not found in registry
            }

            // Check input ports match
            const typeInputs = Object.entries(type.inputTypes);
            const funcInputs = funcDef.inputPortDefinitions;

            if (typeInputs.length !== funcInputs.length) {
                return false;
            }

            // Verify each input matches
            for (const [portName, portType] of typeInputs) {
                const matchingInput = funcInputs.find((input: any) => input.name === portName);
                if (!matchingInput) {
                    return false;
                }
                // Check if the types are compatible (function def type should match expected type)
                if (!typeAMatchesTypeB(matchingInput.type, portType)) {
                    return false;
                }
            }

            // Check output ports match
            const typeOutputs = Object.entries(type.outputTypes);
            const funcOutputs = funcDef.outputPortDefinitions;

            if (typeOutputs.length !== funcOutputs.length) {
                return false;
            }

            // Verify each output matches
            for (const [portName, portType] of typeOutputs) {
                const matchingOutput = funcOutputs.find((output: any) => output.name === portName);
                if (!matchingOutput) {
                    return false;
                }
                // Check if the types are compatible (function def type should match expected type)
                if (!typeAMatchesTypeB(matchingOutput.type, portType)) {
                    return false;
                }
            }

            return true;
        }
        default:
            throw new Error(`Unknown DataType kind: ${(type as any).kind}`);
    }
}
